import React, { useEffect, useState, useRef } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { NavLink, OrderFormData } from '../types';
import logoImage from '../assets/airy.webp';
import { colors, typography, borderRadius, layout } from '../styles/designTokens';
import { saveOrderToGoogleSheets } from '../utils/googleSheetsOrder';

const PaymentSuccessPage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [orderData, setOrderData] = useState<OrderFormData | null>(null);
  const [saveStatus, setSaveStatus] = useState<'saving' | 'saved' | 'error'>('saving');
  const hasSavedRef = useRef(false);

  const navLinks: NavLink[] = [
    { label: 'בית', href: '/', isActive: false },
    { label: 'ממליצים', href: '/#testimonials', isActive: false },
    { label: 'מפרט המכונה', href: '/#product-specs', isActive: false },
    { label: 'נקודות מכירה', href: '/#branches', isActive: false },
    { label: 'צור קשר', href: '/#contact', isActive: false },
  ];

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const response = urlParams.get('response');
    const transactionId = urlParams.get('transaction_id');
    
    const savedOrderData = localStorage.getItem('orderData');
    
    if (response === 'success') {
      setPaymentStatus('success');
      
      if (savedOrderData) {
        const parsedData: OrderFormData = JSON.parse(savedOrderData);
        setOrderData(parsedData);
        
        if (!hasSavedRef.current) {
          hasSavedRef.current = true;
          saveOrderToGoogleSheets(parsedData, transactionId).then(() => {
            setSaveStatus('saved');
          }).catch(() => {
            setSaveStatus('error');
          });
        }
      }
    } else {
      setPaymentStatus('error');
    }
  }, []);

  return (
    <div
      className="min-h-screen scroll-smooth"
      style={{
        fontFamily: typography.fontFamily,
        backgroundColor: '#000000',
      }}
    >
      {/* Skip to main content link */}
      <a
        href="#main-content"
        style={{
          position: 'absolute',
          left: '-9999px',
          zIndex: 999,
          padding: '12px 24px',
          backgroundColor: colors.orangeGold,
          color: '#000000',
          fontWeight: 700,
          fontSize: '1rem',
          textDecoration: 'none',
          borderRadius: '4px',
        }}
        onFocus={(e) => {
          e.currentTarget.style.left = '50%';
          e.currentTarget.style.top = '10px';
          e.currentTarget.style.transform = 'translateX(-50%)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.left = '-9999px';
          e.currentTarget.style.top = 'auto';
          e.currentTarget.style.transform = 'none';
        }}
      >
        דלג לתוכן הראשי
      </a>
      <Header
        logoImage={logoImage}
        navLinks={navLinks}
        isMobileMenuOpen={isMobileMenuOpen}
        onMobileMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      {/* Main Content */}
      <main id="main-content" className="py-8 md:py-12" style={{ 
        paddingTop: 'clamp(90px, calc(5vw + 70px), 110px)',
        paddingBottom: '40px',
      }}>
        <div className="container mx-auto px-4" style={{ maxWidth: layout.contentMaxWidth }}>
          <div className="max-w-2xl mx-auto">
            {/* Back Button - כפתור חזרה */}
            <div style={{ marginBottom: 'clamp(16px, 2.5vw, 24px)' }}>
              <a
                href="/"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: colors.orangeGold,
                  fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  padding: '8px 12px',
                  borderRadius: '8px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = colors.gold.mediumLight;
                  e.currentTarget.style.background = 'rgba(245, 182, 88, 0.1)';
                  e.currentTarget.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = colors.orangeGold;
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.transform = 'translateX(0)';
                }}
              >
                <span style={{ fontSize: '1.2em', lineHeight: '1' }}>→</span>
                <span>חזרה לדף הבית</span>
              </a>
            </div>
            
            {/* Loading State */}
            {paymentStatus === 'loading' && (
              <div 
                className="p-4 md:p-5 rounded-xl"
                role="status"
                aria-live="polite"
                style={{ 
                  backgroundColor: '#000000',
                  border: '2px solid #FFFFFF',
                  borderRadius: borderRadius.soft,
                }}
              >
                <div className="text-center">
                  <div 
                    className="animate-spin rounded-full mx-auto mb-8"
                    style={{
                      width: '128px',
                      height: '128px',
                      border: `4px solid ${colors.backgroundMuted}`,
                      borderTop: `4px solid ${colors.orangeGold}`,
                    }}
                  ></div>
                  <h1 
                    style={{ 
                      color: colors.orangeGold,
                      fontSize: 'clamp(2rem, 5vw, 3rem)',
                      fontWeight: 900,
                      marginBottom: layout.gap.medium,
                    }}
                  >
                    מעבד את התשלום...
                  </h1>
                  <p 
                    style={{ 
                      color: colors.textMuted,
                      fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                      fontWeight: 400,
                    }}
                  >
                    אנא המתן, אנו בודקים את פרטי התשלום
                  </p>
                </div>
              </div>
            )}

            {/* Success State */}
            {paymentStatus === 'success' && (
              <div 
                className="p-4 md:p-5 rounded-xl"
                role="region"
                aria-labelledby="success-heading"
                style={{ 
                  backgroundColor: '#000000',
                  border: '2px solid #FFFFFF',
                  borderRadius: borderRadius.soft,
                }}
              >
                <h1 
                  id="success-heading"
                  className="text-center mb-6 md:mb-8"
                  style={{ 
                    color: colors.orangeGold,
                    fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                    fontWeight: 900,
                    lineHeight: 1.2,
                    letterSpacing: typography.heading.letterSpacing,
                    scrollMarginTop: '80px',
                  }}
                >
                  התשלום בוצע בהצלחה
                </h1>
                
                <div 
                  className="p-4 md:p-5 rounded-xl mb-6"
                  style={{ 
                    backgroundColor: '#1a1a1a',
                    border: `1.5px solid ${colors.orangeGold}`,
                    borderRadius: borderRadius.soft,
                  }}
                >
                  <h2 
                    className="mb-4 text-center"
                    style={{ 
                      color: colors.orangeGold,
                      fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)',
                      fontWeight: 900,
                      lineHeight: 1.2,
                    }}
                  >
                    פרטי ההזמנה
                  </h2>
                  
                  {orderData && (
                    <div className="text-right" style={{ display: 'flex', flexDirection: 'column', gap: layout.gap.medium }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: colors.orangeGold, fontSize: typography.fontSize.lg, fontWeight: 700 }}>
                          שם:
                        </span>
                        <span style={{ color: colors.textPrimary, fontSize: typography.fontSize.lg, fontWeight: 400 }}>
                          {orderData.firstName} {orderData.lastName}
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: colors.orangeGold, fontSize: typography.fontSize.lg, fontWeight: 700 }}>
                          טלפון:
                        </span>
                        <span style={{ color: colors.textPrimary, fontSize: typography.fontSize.lg, fontWeight: 400 }}>
                          {orderData.phone}
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: colors.orangeGold, fontSize: typography.fontSize.lg, fontWeight: 700 }}>
                          כתובת:
                        </span>
                        <span style={{ color: colors.textPrimary, fontSize: typography.fontSize.lg, fontWeight: 400 }}>
                          {orderData.street} {orderData.buildingNumber}
                          {orderData.entrance && `, כניסה ${orderData.entrance}`}
                          {orderData.apartment && `, דירה ${orderData.apartment}`}
                          , {orderData.city}
                        </span>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: colors.orangeGold, fontSize: typography.fontSize.lg, fontWeight: 700 }}>
                          סכום:
                        </span>
                        <span style={{ color: colors.orangeGold, fontSize: typography.fontSize.xl, fontWeight: 900 }}>
                          ₪389
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div 
                  className="p-4 rounded-lg text-center mb-6"
                  style={{ 
                    backgroundColor: '#1a1a1a',
                    border: `1.5px solid ${colors.orangeGold}`,
                  }}
                >
                  <p 
                    style={{ 
                      color: colors.textPrimary,
                      fontSize: typography.fontSize.base,
                      fontWeight: 400,
                      marginBottom: layout.gap.small,
                    }}
                  >
                    קבלה תשלח למייל
                  </p>
                  <p 
                    style={{ 
                      color: colors.textMuted,
                      fontSize: typography.fontSize.base,
                      fontWeight: 400,
                    }}
                  >
                    זמן משלוח עד 7 ימי עסקים בכפוף לתקנון המשלוחים
                  </p>
                </div>


              </div>
            )}

            {/* Error State */}
            {paymentStatus === 'error' && (
              <div 
                className="p-4 md:p-5 rounded-xl"
                role="alert"
                aria-labelledby="error-heading"
                style={{ 
                  backgroundColor: '#000000',
                  border: '2px solid #FFFFFF',
                  borderRadius: borderRadius.soft,
                }}
              >
                <div className="text-center">
                  <div style={{ fontSize: '6rem', marginBottom: layout.gap.large }} aria-hidden="true">❌</div>
                  <h1 
                    id="error-heading"
                    className="mb-6 md:mb-8"
                    style={{ 
                      color: colors.orangeGold,
                      fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                      fontWeight: 900,
                      lineHeight: 1.2,
                      letterSpacing: typography.heading.letterSpacing,
                      scrollMarginTop: '80px',
                    }}
                  >
                    התשלום נכשל
                  </h1>
                  
                  <div 
                    className="p-4 md:p-5 rounded-xl mb-6"
                    style={{ 
                      backgroundColor: '#1a1a1a',
                      border: `1.5px solid ${colors.orangeGold}`,
                      borderRadius: borderRadius.soft,
                    }}
                  >
                    <p 
                      className="mb-4"
                      style={{ 
                        color: colors.textPrimary,
                        fontSize: 'clamp(1rem, 2vw, 1.125rem)',
                        fontWeight: 400,
                      }}
                    >
                      אירעה שגיאה בעיבוד התשלום
                    </p>
                    
                    <p 
                      style={{ 
                        color: colors.textMuted,
                        fontSize: 'clamp(0.95rem, 2vw, 1.125rem)',
                        fontWeight: 400,
                      }}
                    >
                      אנא נסה שוב או צור קשר עם התמיכה
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a 
                      href="/payment" 
                      className="px-8 py-4 rounded-full font-bold text-center transition-all duration-300"
                      style={{ 
                        background: colors.accentSpecial.buttonGradientDiagonal,
                        color: '#000000',
                        fontSize: typography.fontSize.xl,
                        fontWeight: 700,
                        boxShadow: '0 4px 15px rgba(212, 148, 58, 0.3)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(212, 148, 58, 0.5)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 148, 58, 0.3)';
                      }}
                    >
                      נסה שוב
                    </a>
                    
                    <a 
                      href="/#contact" 
                      className="px-8 py-4 rounded-full font-bold text-center transition-all duration-300"
                      style={{ 
                        backgroundColor: '#1a1a1a',
                        border: `1.5px solid ${colors.orangeGold}`,
                        color: colors.orangeGold,
                        fontSize: typography.fontSize.xl,
                        fontWeight: 700,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      צור קשר
                    </a>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
